// wiring.js — pure, unit-testable mapping between the live craftjs Container node
// props and the WL-011 ContainerEditor's controlled `value` object. WL-011b.
//
// WHY A PURE MODULE: the connected adapter (ContainerEditorSettings.jsx) can only be
// exercised inside the running craftjs Editor, so the mapping — the part that can
// silently corrupt a user's container — is extracted here as two pure functions that
// ARE unit-testable in isolation (see harness/wiring.test.mjs). The adapter is then a
// thin useNode() shell over these.
//
// The live craftjs Container node stores PADDING under `spacing`, and border
// color/width under a nested `border` object; the editor's flat `value` normalizes
// those. The editor emits the FULL merged value on every change (onChange({...v,...p})),
// so applyValueToProps writes the whole value, not a delta.

// craftjs node props  ->  ContainerEditor `value`
export function nodePropsToValue(props = {}) {
  const p = props || {};
  return {
    fullWidth: !!p.fullWidth,
    background: p.background ?? "#F3ECFF",
    opacity: p.opacity ?? 100,
    padding: { top: 0, right: 0, bottom: 0, left: 0, ...(p.spacing || {}) },
    applyPaddingAll: !!p.isApplyPaddingOnAllSides,
    margin: { top: 0, right: 0, bottom: 0, left: 0, ...(p.margin || {}) },
    prevMargin: p.prevMargin,
    applyMarginAll: !!p.isApplyMarginOnAllSides,
    radius: p.radius ?? 8,
    borderColor: (p.border && p.border.color) ?? "#E4E7EC",
    borderWidth: (p.border && p.border.width) ?? 0,
    dropShadowColor: p.dropShadowColor ?? "",
    // CONTAINER_BACKGROUNDS: the structured Image/GIF · Gradient fill (undefined for
    // a plain solid-color container). Passed through verbatim so the editor can show
    // the current fill; a solid-color container leaves this undefined.
    containerFill: p.containerFill,
  };
}

// ContainerEditor `value`  ->  mutate a craftjs draft props object in place.
// Used inside setProp((props) => applyValueToProps(next, props)). Returns the same
// draft so callers can also read it (e.g. to sync the reducer copy).
export function applyValueToProps(next = {}, props = {}) {
  if (!props.border) props.border = {};
  if (!props.spacing) props.spacing = {};
  if (!props.margin) props.margin = {};

  props.fullWidth = !!next.fullWidth;
  props.background = next.background;
  props.opacity = next.opacity;
  props.spacing = { ...props.spacing, ...(next.padding || {}) };
  props.isApplyPaddingOnAllSides = !!next.applyPaddingAll;
  props.margin = { ...props.margin, ...(next.margin || {}) };
  if (next.prevMargin !== undefined) props.prevMargin = next.prevMargin;
  props.isApplyMarginOnAllSides = !!next.applyMarginAll;
  props.radius = next.radius;
  props.border = { ...props.border, color: next.borderColor, width: next.borderWidth };
  props.dropShadowColor = next.dropShadowColor || null;
  props.isFirstContainer = false;

  // CONTAINER_BACKGROUNDS (additive, flag-gated). Tri-state on `next.containerFill`:
  //   • undefined -> NOT provided (the flag-OFF editor never sets it) -> DO NOT
  //     touch the node shape, so a plain container stays byte-identical.
  //   • null / falsy -> explicit clear (Color type selected) -> remove the prop so
  //     the renderer falls back to the legacy solid-color path.
  //   • object -> a structured Image/Gradient fill -> persist it verbatim.
  if (next.containerFill !== undefined) {
    if (next.containerFill) props.containerFill = next.containerFill;
    else delete props.containerFill;
  }
  return props;
}

const wiring = { nodePropsToValue, applyValueToProps };
export default wiring;
